// MARCA | 06/05/2026 | New file – Cart progress bar custom element with milestone tracking
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} Refs
 * @property {HTMLDivElement} progressFill
 * @property {HTMLDivElement} milestone1
 * @property {HTMLDivElement} milestone2
 * @property {HTMLDivElement} milestone3
 * @property {HTMLDivElement} milestone1Spend
 * @property {HTMLDivElement} milestone2Spend
 * @property {HTMLDivElement} milestone3Spend
 * @property {HTMLDivElement} milestone1Msg
 * @property {HTMLDivElement} milestone2Msg
 * @property {HTMLDivElement} milestone3Msg
 *
 * @extends {Component<Refs>}
 */
class CartProgressBarComponent extends Component {
  /** @type {{ threshold: number, label: string, ref: string, spendRef: string, reachedPrefix: string, reachedHighlight: string, msgRef: string }[]} */
  #milestones = [];

  /** @type {number[]} Sorted thresholds in cents */
  #sortedThresholds = [];

  /** @type {number} Size of each equal segment as a percentage */
  #segmentSize = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#milestones = [
      {
        threshold: parseFloat(this.dataset.m1Threshold || "0"),
        label: this.dataset.m1Label || "",
        ref: "milestone1",
        spendRef: "milestone1Spend",
        reachedPrefix: this.dataset.m1ReachedPrefix || "",
        reachedHighlight: this.dataset.m1ReachedHighlight || "",
        msgRef: "milestone1Msg",
      },
      {
        threshold: parseFloat(this.dataset.m2Threshold || "0"),
        label: this.dataset.m2Label || "",
        ref: "milestone2",
        spendRef: "milestone2Spend",
        reachedPrefix: this.dataset.m2ReachedPrefix || "",
        reachedHighlight: this.dataset.m2ReachedHighlight || "",
        msgRef: "milestone2Msg",
      },
      {
        threshold: parseFloat(this.dataset.m3Threshold || "0"),
        label: this.dataset.m3Label || "",
        ref: "milestone3",
        spendRef: "milestone3Spend",
        reachedPrefix: this.dataset.m3ReachedPrefix || "",
        reachedHighlight: this.dataset.m3ReachedHighlight || "",
        msgRef: "milestone3Msg",
      },
    ].filter((m) => m.threshold > 0);

    this.#sortedThresholds = this.#milestones
      .map((m) => m.threshold * 100)
      .sort((a, b) => a - b);

    this.#segmentSize =
      this.#milestones.length > 0 ? 100 / this.#milestones.length : 100;

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.#handleCartUpdate,
    );

    this.#fetchAndUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate,
    );
    document.removeEventListener(
      ThemeEvents.discountUpdate,
      this.#handleCartUpdate,
    );
  }

  /** @param {Event} event */
  #handleCartUpdate = (event) => {
    const cartData = /** @type {any} */ (event).detail?.resource;

    if (cartData && typeof cartData.total_price !== "undefined") {
      this.#updateFromCart(cartData);
    } else {
      this.#fetchAndUpdate();
    }
  };

  #fetchAndUpdate = async () => {
    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();
      this.#updateFromCart(cart);
    } catch (error) {
      // Cart data will arrive via next event
    }
  };

  /** @param {{ total_price: number }} cart */
  #updateFromCart(cart) {
    if (
      !cart ||
      typeof cart.total_price === "undefined" ||
      cart.total_price === null
    )
      return;

    const cartCents = cart.total_price;
    if (isNaN(cartCents) || !isFinite(cartCents)) return;
    if (this.#sortedThresholds.length === 0) return;

    const cartValue = cartCents / 100;
    const progressPercentage = this.#calculateFill(cartCents);

    this.#updateDisplay(progressPercentage, cartValue);
  }

  /**
   * @param {number} cartCents
   * @returns {number}
   */
  #calculateFill(cartCents) {
    const thresholds = this.#sortedThresholds;
    let completedSegments = 0;

    for (let i = 0; i < thresholds.length; i++) {
      const current = thresholds[i] ?? 0;
      if (cartCents >= current) {
        completedSegments++;
      } else {
        const prev = i === 0 ? 0 : (thresholds[i - 1] ?? 0);
        const segmentRange = current - prev;
        const segmentProgress = cartCents - prev;
        const fraction = segmentRange > 0 ? segmentProgress / segmentRange : 0;
        return Math.min(
          100,
          completedSegments * this.#segmentSize + fraction * this.#segmentSize,
        );
      }
    }

    return 100;
  }

  /**
   * @param {number} progressPercentage
   * @param {number} cartValue
   */
  #updateDisplay(progressPercentage, cartValue) {
    const { progressFill } = this.refs;

    if (progressFill) {
      requestAnimationFrame(() => {
        progressFill.style.width = `${progressPercentage}%`;
      });
    }

    for (const m of this.#milestones) {
      const el = /** @type {HTMLElement | undefined} */ (this.refs[m.ref]);
      const spendEl = /** @type {HTMLElement | undefined} */ (
        this.refs[m.spendRef]
      );
      if (!el) continue;

      const unlocked = cartValue >= m.threshold;

      if (unlocked) {
        el.classList.add("cart-progress-milestone--unlocked");
      } else {
        el.classList.remove("cart-progress-milestone--unlocked");

        if (spendEl) {
          const amountSpan = spendEl.querySelector(
            ".cart-progress-milestone__spend-amount",
          );
          if (amountSpan) {
            const remaining = (m.threshold - cartValue) * 100;
            amountSpan.textContent = `spend ${this.#formatMoney(remaining)} for`;
          }
        }
      }
    }
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const shopify = /** @type {any} */ (window.Shopify);
    const win = /** @type {any} */ (window);
    if (typeof shopify !== "undefined" && shopify.formatMoney) {
      return shopify.formatMoney(
        cents,
        win.Theme?.moneyFormat || "£{{amount}}",
      );
    }
    return `£${(cents / 100).toFixed(2)}`;
  }
}

if (!customElements.get('cart-progress-bar')) {
  customElements.define('cart-progress-bar', CartProgressBarComponent);
}
